import React, { useState } from "react";
import { Box, Typography, Paper, Alert, Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import HistoryIcon from "@mui/icons-material/History";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { T, computeChecklistResult } from "./checklistScoring";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function fmtClock(hhmm) {
  if (!hhmm) return "—";
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

function ResultChip({ status, pct }) {
  const color = status === "pass" ? T.performed : status === "fail" ? T.missed : T.na;
  const bg = status === "pass" ? "var(--color-success-bg)" : status === "fail" ? "var(--color-danger-bg)" : "var(--color-surface-2)";
  const label = status === "pass" ? `Pass · ${Math.round(pct)}%` : status === "fail" ? `Fail · ${Math.round(pct)}%` : `${Math.round(pct)}% so far`;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: 11.5, fontWeight: 700, px: "9px", py: "4px", borderRadius: "999px", bgcolor: bg, color }}>
      {status === "pass" && <CheckIcon sx={{ fontSize: 13 }} />}
      {status === "fail" && <WarningAmberIcon sx={{ fontSize: 13 }} />}
      {label}
    </Box>
  );
}

function AttemptRow({ attempt, items, passingScore }) {
  const [open, setOpen] = useState(false);
  const r = computeChecklistResult(items, attempt.scores || {}, passingScore);
  const notes = attempt.notes || {};
  const noteEntries = Object.entries(notes).filter(([, text]) => text);

  return (
    <Box sx={{ border: `1px solid ${T.border}`, borderRadius: "10px", overflow: "hidden", bgcolor: "#fff" }}>
      <Box
        component="button"
        onClick={() => setOpen((o) => !o)}
        sx={{ width: "100%", display: "flex", alignItems: "center", gap: "12px", p: "10px 14px", bgcolor: "transparent", border: "none", cursor: "pointer", font: "inherit", textAlign: "left" }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
            {attempt.evaluatorId ? `Practised with ${attempt.evaluatorName || "classmate"}` : "Solo practice"}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: T.na, mt: "1px" }}>
            {fmtDate(attempt.examDate)} · {fmtClock(attempt.startTime)}{attempt.overTime && <span data-testid="over-time-flag"> · Over time limit</span>}
          </Typography>
        </Box>
        <ResultChip status={r.status} pct={r.total > 0 ? (r.earned / r.total) * 100 : 0} />
        <Box sx={{ color: T.na, display: "flex" }}>
          {open ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
        </Box>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: "14px", pb: "14px", pt: "4px", borderTop: `1px solid ${T.border}`, bgcolor: T.surface2 }}>
          <Box sx={{ display: "flex", gap: "16px", my: "10px", fontSize: 12 }}>
            <Box><Box component="span" sx={{ color: T.na }}>Scored </Box><Box component="strong">{r.scoredCount}/{items.length}</Box></Box>
            <Box><Box component="span" sx={{ color: T.na }}>Points </Box><Box component="strong">{r.earned}/{r.total}</Box></Box>
            {r.criticalsFailed.length > 0 && <Box sx={{ color: T.missed, fontWeight: 600 }}>Critical item missed</Box>}
          </Box>
          {attempt.summaryNote && (
            <Box sx={{ fontSize: 12.5, color: T.ink, bgcolor: "#fff", border: `1px solid ${T.border}`, borderRadius: "8px", p: "10px 12px", lineHeight: 1.5, mb: "8px" }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.na, mb: "4px" }}>
                Summary note
              </Typography>
              {attempt.summaryNote}
            </Box>
          )}
          {noteEntries.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {noteEntries.map(([itemId, text]) => {
                const item = items.find((i) => String(i.id) === String(itemId));
                if (!item) return null;
                return (
                  <Box key={itemId} sx={{ fontSize: 12, color: T.ink, display: "flex", gap: "8px" }}>
                    <Box component="span" sx={{ color: T.na, flexShrink: 0 }}>
                      {(item.criteria || "").slice(0, 34)}{(item.criteria || "").length > 34 ? "…" : ""}:
                    </Box>
                    <span>{text}</span>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function PracticeHistoryPanel({ candidateName, attempts, items, passingScore }) {
  const [expanded, setExpanded] = useState(true);
  const sorted = [...(attempts || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const latest = sorted[0];
  const latestResult = latest ? computeChecklistResult(items, latest.scores || {}, passingScore) : null;

  return (
    <Paper elevation={0} sx={{ borderRadius: "14px", border: `1.5px solid ${sorted.length ? "#C7D2FE" : T.border}`, overflow: "hidden", mb: 2.5 }}>
      <Box
        component="button"
        onClick={() => setExpanded((e) => !e)}
        sx={{ width: "100%", display: "flex", alignItems: "center", gap: "12px", p: "14px 18px", bgcolor: sorted.length ? "var(--color-primary-10)" : T.surface2, border: "none", cursor: "pointer", font: "inherit", textAlign: "left" }}
      >
        <Box sx={{ color: T.na, display: "flex" }}>{expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}</Box>
        <Box sx={{ width: 32, height: 32, borderRadius: "9px", bgcolor: "#fff", color: T.primary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <HistoryIcon sx={{ fontSize: 17 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: T.ink, fontFamily: "var(--font-display)" }}>Practice history — {candidateName}</Typography>
          <Typography sx={{ fontSize: 12, color: T.na, mt: "2px" }}>
            {sorted.length === 0
              ? "No peer practice evaluations logged yet"
              : `${sorted.length} practice evaluation${sorted.length === 1 ? "" : "s"} logged`}
            {latestResult && (
              <> · latest{" "}
                <Box component="strong" sx={{ color: latestResult.status === "pass" ? T.performed : latestResult.status === "fail" ? T.missed : T.ink }}>
                  {latestResult.status === "pending" ? `${Math.round((latestResult.earned / (latestResult.total || 1)) * 100)}%` : latestResult.status}
                </Box>
              </>
            )}
          </Typography>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: "4px 18px 16px" }}>
          {sorted.length === 0 ? (
            <Alert severity="info" icon={false} sx={{ borderRadius: "10px", fontSize: 13 }}>
              This student hasn't completed a peer practice evaluation for this skill sheet yet.
            </Alert>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {sorted.map((a) => (
                <AttemptRow key={a.id} attempt={a} items={items} passingScore={passingScore} />
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
